import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Cpu, ThumbsUp, ThumbsDown, ChevronRight } from "lucide-react";
import { InteractiveMarkdown } from "@/components/help/InteractiveMarkdown";
import { TableOfContents } from "@/components/help/TableOfContents";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";

export default function HelpArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [readingProgress, setReadingProgress] = useState(0);
  const [scrollPercentage, setScrollPercentage] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = window.scrollY;
      const progress = (scrolled / documentHeight) * 100;
      setScrollPercentage(Math.min(progress, 100));
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (slug) {
      const saved = localStorage.getItem(`help-progress-${slug}`);
      if (saved) {
        setReadingProgress(parseFloat(saved));
      }
    }
  }, [slug]);

  useEffect(() => {
    if (slug && scrollPercentage > readingProgress) {
      setReadingProgress(scrollPercentage);
      localStorage.setItem(`help-progress-${slug}`, String(scrollPercentage));
    }
  }, [slug, scrollPercentage, readingProgress]);

  const handleFeedback = (helpful: boolean) => {
    toast({
      title: helpful ? "Thank you!" : "Thanks for your feedback",
      description: helpful
        ? "We're glad this article was helpful."
        : "We'll work on improving this article.",
    });
  };

  const { data: article, isLoading } = useQuery({
    queryKey: ["help-article", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("help_articles")
        .select("*")
        .eq("slug", slug)
        .single();

      if (error) throw error;

      // Increment view count
      await supabase
        .from("help_articles")
        .update({ view_count: (data.view_count || 0) + 1 })
        .eq("id", data.id);

      return data;
    },
    enabled: !!slug,
  });

  const { data: relatedArticles = [] } = useQuery({
    queryKey: ["related-articles", article?.category],
    queryFn: async () => {
      if (!article) return [];

      const { data, error } = await supabase
        .from("help_articles")
        .select("*")
        .eq("category", article.category)
        .neq("id", article.id)
        .limit(3);

      if (error) throw error;
      return data;
    },
    enabled: !!article,
  });

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12 text-muted-foreground">
          Loading article...
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="container mx-auto p-6">
        <Card className="p-12 text-center">
          <h2 className="text-2xl font-semibold mb-4">Article Not Found</h2>
          <p className="text-muted-foreground mb-6">
            The help article you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate("/help")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Help Center
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <>
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-muted z-50">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${scrollPercentage}%` }}
        />
      </div>

      <div className="container mx-auto p-6 max-w-7xl">
        {/* Back Button & Breadcrumb */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate("/help")} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Help Center
          </Button>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/help" className="hover:text-primary">
              Help Center
            </Link>
            <ChevronRight className="h-4 w-4" />
            <span>{article.category}</span>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground">{article.title}</span>
          </div>
        </div>

        {/* Article Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Badge>{article.category}</Badge>
            {article.subcategory && (
              <Badge variant="secondary">{article.subcategory}</Badge>
            )}
            {readingProgress > 0 && readingProgress < 100 && (
              <Badge variant="outline">{Math.round(readingProgress)}% complete</Badge>
            )}
          </div>
          <h1 className="text-4xl font-bold mb-4">{article.title}</h1>
          <p className="text-xl text-muted-foreground">{article.summary}</p>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-[1fr_300px] gap-8 mb-8">
          {/* Main Content */}
          <div className="space-y-8">
            <Card className="p-8">
              <InteractiveMarkdown content={article.content} articleSlug={slug!} />
            </Card>

            {/* Feedback Section */}
            <Card className="p-6">
              <h3 className="font-semibold mb-4">Was this article helpful?</h3>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => handleFeedback(true)}
                  className="gap-2"
                >
                  <ThumbsUp className="h-4 w-4" />
                  Yes
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleFeedback(false)}
                  className="gap-2"
                >
                  <ThumbsDown className="h-4 w-4" />
                  No
                </Button>
              </div>
            </Card>

            {/* Ask Bridgette CTA */}
            <Card className="p-6 bg-gradient-to-br from-primary/10 to-primary/5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold mb-1">Still have questions?</h3>
                  <p className="text-muted-foreground">
                    Ask Bridgette for personalized help
                  </p>
                </div>
                <Link to="/agent">
                  <Button>
                    <Cpu className="mr-2 h-4 w-4" />
                    Ask Bridgette
                  </Button>
                </Link>
              </div>
            </Card>
          </div>

          {/* Sidebar - Table of Contents */}
          <div className="hidden lg:block">
            <TableOfContents content={article.content} />
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {relatedArticles.map((related) => (
                <Link key={related.id} to={`/help/${related.slug}`}>
                  <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full">
                    <h3 className="font-semibold mb-2">{related.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {related.summary}
                    </p>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
